import styled from 'styled-components';
import { exFont } from '../../shared/typography';
import { ExamsScope } from '../../shared/theme';
import { StepPage } from '../YourMarksStep/style';

// The prototypes inherit the browser's normal line height; the app's body
// sets 1, which made every pill and row a few px shorter than the design.
export const ResultsScope = styled(ExamsScope)`
  line-height: normal;
`;

// Results header, chips, tabs and modals (tplA 1237–1259, 1715–1733).
export const ResultsPage = styled(StepPage)`
  max-width: 1000px;
  padding: 28px 24px 44px;
  gap: 14px;
`;

export const Header = styled.div`
  min-height: 40px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
`;

export const Title = styled.h2`
  ${exFont(700, 24)}
  line-height: 32px;
  margin: 0;
  color: var(--ex-ink);
`;

export const Grow = styled.span`
  flex: 1 1 auto;
`;

// tplA 1255 (Show names) / 1256 (Download & share): $accent = purple outline.
export const HeaderButton = styled.button`
  ${({ $accent }) => exFont(600, $accent ? 14.5 : 14)}
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: ${({ $accent }) => ($accent ? '10px 18px' : '10px 16px')};
  border: ${({ $accent }) =>
    $accent
      ? '1.5px solid var(--ex-purple)'
      : '1px solid var(--ex-line-strong)'};
  border-radius: 999px;
  background: var(--ex-surface);
  color: ${({ $accent }) => ($accent ? 'var(--ex-purple)' : '#3d3847')};
  white-space: nowrap;
  cursor: pointer;
  &:hover {
    background: var(--ex-surface-2);
  }
  &:focus-visible {
    outline: 2px solid var(--ex-purple);
    outline-offset: 2px;
  }
`;

export const MenuWrap = styled.div`
  position: relative;
`;

export const Menu = styled.div`
  position: absolute;
  right: 0;
  top: calc(100% + 6px);
  z-index: 30;
  width: 270px;
  box-sizing: border-box;
  padding: 6px;
  background: var(--ex-surface);
  border: 1px solid var(--ex-line);
  border-radius: 14px;
  box-shadow: var(--ex-shadow-3);
`;

// tplA 1239: "View questions" next to the title.
export const ViewQuestionsButton = styled.button`
  ${exFont(600, 13.5)}
  line-height: 1;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  height: 32px;
  box-sizing: border-box;
  padding: 0 14px;
  border: 1.5px solid #d9c6fb;
  border-radius: 999px;
  background: var(--ex-surface);
  color: var(--ex-purple);
  white-space: nowrap;
  cursor: pointer;
  &:hover {
    border-color: var(--ex-purple);
  }
  &:focus-visible {
    outline: 2px solid var(--ex-purple);
    outline-offset: 2px;
  }
`;

export const MenuItem = styled.button`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  width: 100%;
  padding: 10px 12px;
  text-align: left;
  border: none;
  border-radius: 8px;
  background: transparent;
  cursor: pointer;
  &:hover,
  &:focus-visible {
    background: #f7f3fe;
    outline: none;
  }
`;

export const MenuTitle = styled.span`
  ${exFont(600, 14)}
  color: var(--ex-ink);
`;

export const MenuSub = styled.span`
  ${exFont(400, 12.5)}
  color: var(--ex-ink-2);
`;

// tplA 1244–1254: the calibration popover.
export const CalibrationPopover = styled.div`
  position: absolute;
  top: 40px;
  left: 0;
  z-index: 41;
  width: 380px;
  max-width: calc(100vw - 32px);
  max-height: calc(100vh - 220px);
  overflow: auto;
  box-sizing: border-box;
  padding: 16px 18px;
  background: var(--ex-surface);
  border: 1px solid var(--ex-line);
  border-radius: 14px;
  box-shadow: 0 14px 36px rgba(48, 27, 114, 0.2);
`;

export const CalibrationHead = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 6px;
`;

export const CalibrationTitle = styled.p`
  ${exFont(700, 16)}
  flex: 1;
  margin: 0;
  color: var(--ex-ink);
`;

export const CalibrationClose = styled.button`
  display: flex;
  padding: 0;
  border: none;
  background: none;
  color: var(--ex-ink);
  cursor: pointer;
`;

export const CalibrationEffect = styled.p`
  ${exFont(400, 13.5)}
  margin: 0 0 12px;
  line-height: 1.5;
  color: var(--ex-ink-strong);
`;

export const CalibrationSamples = styled.div`
  margin-bottom: 12px;
  overflow: hidden;
  border: 1px solid var(--ex-line);
  border-radius: 10px;
`;

export const CalibrationSample = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 12px;
  & + & {
    border-top: 1px solid #f2f0f6;
  }
  & > span:first-child {
    ${exFont(500, 14)}
    flex: 1;
    min-width: 0;
    color: var(--ex-ink);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  & > span:nth-child(2) {
    ${exFont(700, 14)}
    min-width: 48px;
    text-align: right;
    color: var(--ex-ink);
  }
  & > button {
    ${exFont(600, 13)}
    padding: 0;
    border: none;
    background: none;
    color: var(--ex-purple);
    cursor: pointer;
  }
`;

export const CalibrationAdd = styled.button`
  ${exFont(600, 14.5)}
  width: 100%;
  padding: 11px 18px;
  border: none;
  border-radius: 999px;
  background: var(--ex-purple);
  color: var(--white);
  cursor: pointer;
`;

export const CalibrationNote = styled.p`
  ${exFont(400, 12.5)}
  margin: 12px 0 0;
  line-height: 1.5;
  color: var(--ex-ink-2);
`;

export const ChipsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
`;

// tplA 1258 / 2728.
export const ScopeChip = styled.button`
  ${exFont(600, 13.5)}
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 34px;
  box-sizing: border-box;
  padding: 0 14px;
  border-radius: 999px;
  border: 1.5px solid
    ${({ $active }) => ($active ? 'var(--ex-purple)' : 'var(--ex-line-strong)')};
  background: ${({ $active }) =>
    $active ? 'var(--ex-purple)' : 'var(--ex-surface)'};
  color: ${({ $active }) => ($active ? 'var(--white)' : '#3d3847')};
  white-space: nowrap;
  cursor: pointer;
`;

export const ChipSub = styled.span`
  ${exFont(500, 12)}
  line-height: 1;
  opacity: 0.75;
`;

export const TabsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  border-bottom: 1px solid var(--ex-line);
`;

// tplA 1259 / 2836.
export const Tab = styled.button`
  ${exFont(600, 15)}
  padding: 10px 14px;
  border: none;
  border-bottom: 2.5px solid
    ${({ $active }) => ($active ? 'var(--ex-purple)' : 'transparent')};
  background: none;
  color: ${({ $active }) =>
    $active ? 'var(--ex-purple)' : 'var(--ex-ink-strong)'};
  white-space: nowrap;
  cursor: pointer;
  margin-bottom: -1px;
`;

export const RulesLink = styled.button`
  ${exFont(600, 13.5)}
  padding: 0;
  border: none;
  background: none;
  color: var(--ex-purple);
  white-space: nowrap;
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
  &:focus-visible {
    outline: 2px solid var(--ex-purple);
    outline-offset: 2px;
  }
`;

// tplA 1259 (rulesOpen).
export const RulesPanel = styled.div`
  ${exFont(400, 14)}
  line-height: 1.5;
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 12px 16px;
  border: 1px solid var(--ex-line);
  border-radius: 14px;
  background: var(--ex-surface);
  color: var(--ex-ink);
  b {
    font-weight: 700;
  }
`;

export const ModalCard = styled.div`
  width: min(760px, calc(100vw - 32px));
  max-height: calc(100vh - 64px);
  overflow: auto;
  background: var(--white);
  border-radius: 16px;
  box-shadow: var(--ex-shadow-modal);
  padding: 20px 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  /* focused as a container by useModalFocus; its controls keep their rings */
  &:focus {
    outline: none;
  }
`;

export const ModalHead = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
`;

export const ModalTitle = styled.p`
  ${exFont(600, 'xl')}
  margin: 0;
`;

export const ModalSub = styled.p`
  ${exFont(400, 's')}
  margin: 2px 0 0;
  color: var(--ex-ink-2);
`;

export const QuestionItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 12px 0;
  border-bottom: 1px solid var(--ex-line);
  p {
    margin: 0;
  }
`;

export const QuestionMeta = styled.div`
  ${exFont(600, 's')}
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const QuestionBody = styled.p`
  ${exFont(400, 'm')}
  color: var(--ex-ink);
  line-height: 1.5;
`;

export const RubricTable = styled.table`
  ${exFont(400, 's')}
  width: 100%;
  border-collapse: collapse;
  th,
  td {
    padding: 8px;
    border: 1px solid var(--ex-line);
    vertical-align: top;
    text-align: left;
  }
  th {
    ${exFont(600, 's')}
    background: var(--ex-surface-2);
  }
`;

export const BannerLine = styled.div`
  ${exFont(500, 's')}
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  border-radius: var(--ex-radius-item);
  color: var(--ex-info-ink);
  background: var(--ex-info-bg);
`;
